// Fleet's coverage (components/FleetEndpoints.jsx), from its read (apiClient
// getFleetCoverage): of the endpoints in the inventory, how many are new in
// the block (first seen in it), how many reported in it and how many did not,
// and the sessions from endpoints. An endpoint reported if it was heard from
// since the block began: a heartbeat (each endpoint's latest, kept 30 days),
// a session it made, or a change to its inventory. Not every agent sends
// heartbeats, so the rest count too; an old inventory says nothing either way.

/** Whose an endpoint is, as written: a Windows account loses its machine or domain ("CORP\\ana" is "ana"). */
export const ownerOf = (raw) => String(raw ?? "").replace(/^.*\\/, "").trim();

/** The Endpoints page's totals for a block: each null where it could not be read. */
export function coverageOf(d) {
  const from = Date.parse(d?.window?.now?.from);
  const total = Number.isFinite(d?.total) ? d.total : null;
  const newIn = d?.firstSeen ? d.firstSeen.filter((e) => e.firstSeenMs >= from).length : null;
  const heard = d?.lastSeen || d?.active || d?.changed
    ? new Set([...(d.lastSeen || []).filter((e) => e.lastSeenMs >= from).map((e) => String(e.id)), ...(d.active || []), ...(d.changed || [])].map(String))
    : null;
  const reporting = heard ? heard.size : null;
  return {
    total,
    // The inventory before the block: today's, less what was new in it.
    totalThen: total != null && newIn != null ? total - newIn : null,
    newIn,
    reporting,
    quiet: total != null && reporting != null ? Math.max(0, total - reporting) : null,
    heard,
    sessions: d?.now?.sessions ?? null,
    sessionsThen: d?.then?.sessions ?? null,
    firstSeen: new Map((d?.firstSeen || []).map((e) => [String(e.id), e.firstSeenMs])),
    lastSeen: new Map((d?.lastSeen || []).map((e) => [String(e.id), e.lastSeenMs])),
  };
}

/** The ids newest first, by when each was first seen. */
export function newestIds(firstSeen) {
  return [...firstSeen].sort((a, b) => b[1] - a[1] || String(a[0]).localeCompare(String(b[0]))).map(([id]) => id);
}

/**
 * An endpoint row of the register (lib/fleetList.js rows): its name, whose it
 * is, its sessions in the block, and what made them, split by agent (paths,
 * lib/fleetBreakdown.js toPaths, each with its `endpoint`).
 */
export function endpointRowOf(row, agents = []) {
  const by = new Map();
  for (const p of agents) {
    if (p.endpoint !== row.id || !p.sessions) continue;
    const part = by.get(p.agent) || { id: p.agent, name: p.agentName || p.agent, sessions: 0 };
    part.sessions += p.sessions;
    by.set(p.agent, part);
  }
  return {
    id: row.id,
    name: row.hostname || "Unnamed endpoint",
    owner: ownerOf(row.registeredUser),
    sessions: Number(row.sessions) || 0,
    used: [...by.values()].sort((a, b) => b.sessions - a.sessions || a.name.localeCompare(b.name)),
    inventory: row,
  };
}

/** The endpoints' sessions in the block before, by id, as the register reads change (lib/fleetList.js beforeOf). */
export function endpointsBefore(then) {
  if (!then) return null;
  const by = new Map();
  for (const r of then) {
    const id = String(r.endpoint || "");
    by.set(id, { sessions: (by.get(id)?.sessions || 0) + (Number(r.sessions) || 0), used: new Map() });
  }
  return by;
}
